import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { Alert, Platform } from 'react-native';
import { BACKEND_URL } from '../Config/Const';
import Storage from '../utils/Storage';
import ProductosFaltantesModal from '../components/Common/ProductosFaltantesModal';
import ProductosBorradosModal from '../components/Common/ProductosBorradosModal';
import FeedbackModal from '../components/Common/FeedbackModal';

// Crear el contexto para el carrito
const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // Estado para los productos del carrito que vienen de la API
  const [productos, setProductos] = useState([]);
  // Estado para controlar la visibilidad del carrito
  const [carritoVisible, setCarritoVisible] = useState(false);
  // Estado para la carga
  const [loading, setLoading] = useState(false);
  // Estado para errores
  const [error, setError] = useState(null);
  // Estado para los totales del carrito
  const [totales, setTotales] = useState({ subtotal: 0, descuentos: 0, total: 0, precioFlete: 0, percepciones: 0 });
  // Estado para productos faltantes
  const [productosFaltantes, setProductosFaltantes] = useState([]);
  const [mostrarModalFaltantes, setMostrarModalFaltantes] = useState(false);
  const [sucursalAnterior, setSucursalAnterior] = useState(null);
  // Estado para productos borrados
  const [productosBorrados, setProductosBorrados] = useState([]);
  const [mostrarModalBorrados, setMostrarModalBorrados] = useState(false);
  // Estado para modal de feedback de errores
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackTitle, setFeedbackTitle] = useState('');

  // Obtener datos del usuario de forma centralizada
  const getUserData = async () => {
    const mail = await Storage.getItem('mail') || '';
    const codUsu = await Storage.getItem('codUsu') || '';
    const suc = await Storage.getItem('sucursalId') || '4';
    return { mail, codUsu, suc };
  };

  // MEDIDA DE EMERGENCIA: DESACTIVACIÓN COMPLETA DE EVENTOS
  // Esta función ya no emite eventos para romper todos los ciclos posibles
  // Se mantiene la interfaz para compatibilidad con código existente
  const emitCarritoActualizado = () => {
    // NO EMITIR EVENTOS - Esta acción ha sido deliberadamente desactivada
    // Los componentes ahora deben confiar solo en el contexto como fuente de verdad
    return;
  };

  // Función de carga manual que se puede llamar desde botones de UI
  const recargarCarritoManual = async () => {
    // Esta función se puede llamar explícitamente desde la UI cuando el usuario
    // quiera actualizar el carrito manualmente
    return await cargarProductosCarritoSafe();
  };

  // Función para cargar productos del carrito desde la API
  const cargarProductosCarrito = async () => {
    try {
      setLoading(true);
      setError(null);

      const { mail, codUsu, suc } = await getUserData();

      // Si no hay mail, no podemos cargar el carrito
      if (!mail) {
        setProductos([]);
        setTotales({ subtotal: 0, descuentos: 0, total: 0, precioFlete: 0, percepciones: 0 });
        setLoading(false);
        return { success: false, productos: [] };
      }

      // Construir la URL con query parameters
      const queryParams = new URLSearchParams({ mail, codUsu, suc }).toString();
      const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      const responseText = await response.text();

      try {
        const data = JSON.parse(responseText);
        const productosData = data.dsResultado.productos || [];
        setProductos(productosData);

        // Obtener el precio de flete más alto de todos los productos
        const precioFleteTotal = productosData.reduce((maxFlete, producto) => {
          const fleteProducto = producto.precioFlete || 0;
          return Math.max(maxFlete, fleteProducto);
        }, 0);

        // Guardar información de totales si está disponible
        if (data.dsResultado.total && data.dsResultado.total.length > 0) {
          const { subtotal = 0, descuentos = 0, total = 0, percepciones = 0 } = data.dsResultado.total[0];
          setTotales({ subtotal, descuentos, total, precioFlete: precioFleteTotal, percepciones });
        } else {
          // Si no hay totales en la respuesta, solo actualizar el flete
          setTotales(prev => ({ ...prev, precioFlete: precioFleteTotal }));
        }

        // Verificar si hay productos faltantes
        if (data.dsResultado.productosFaltantes && data.dsResultado.productosFaltantes.length > 0) {
          setProductosFaltantes(data.dsResultado.productosFaltantes);
          setMostrarModalFaltantes(true);
        }

        // Verificar si hay productos borrados
        if (data.dsResultado.productosBorrados && data.dsResultado.productosBorrados.length > 0) {
          setProductosBorrados(data.dsResultado.productosBorrados);
          setMostrarModalBorrados(true);
        }

        emitCarritoActualizado();
        return { success: true, productos: productosData };
      } catch (e) {
        setError('Error al obtener datos del carrito');
        setProductos([]);
        return { success: false, error: 'Error al parsear datos', productos: [] };
      }
    } catch (err) {
      setError(`Error de conexión: ${err.message}`);
      setProductos([]);
      return { success: false, error: err.message, productos: [] };
    } finally {
      setLoading(false);
    }
  };

  // Caché local para las verificaciones de productos - evita llamadas repetidas
  const productosCacheRef = useRef(new Map());
  const CACHE_PRODUCTO_TTL = 5000; // 5 segundos de validez

  // Función para verificar si un producto específico está en el carrito - evitando llamadas API
  const verificarProductoEnCarrito = async (plu) => {
    if (!plu) return { estaEnCarrito: false, cantidad: 0 };

    try {
      const now = Date.now();
      const pluStr = String(plu);
      const cache = cacheRef.current;

      // 1. Verificar caché de producto específico primero (más rápido)
      if (productosCacheRef.current.has(pluStr)) {
        const productoCache = productosCacheRef.current.get(pluStr);
        if (now - productoCache.timestamp < CACHE_PRODUCTO_TTL) {
          return productoCache.data;
        }
      }

      // 2. Revisar en el estado actual sin llamar a la API
      if (productos && productos.length > 0) {
        const productoEnCarrito = productos.find(item => String(item.plu) === pluStr);
        if (productoEnCarrito) {
          const result = {
            estaEnCarrito: true,
            cantidad: productoEnCarrito.cantCarrito || 1,
            producto: productoEnCarrito
          };

          // Guardar en caché local
          productosCacheRef.current.set(pluStr, {
            timestamp: now,
            data: result
          });

          return result;
        }
      }

      // 3. Si estamos en un periodo de bloqueo, no realizar más verificaciones
      if (now < cache.blockedUntil) {
        return { estaEnCarrito: false, cantidad: 0, bloqueado: true };
      }

      // 4. Si hay demasiadas verificaciones recientes, usar caché o devolver resultado vacío
      if (cache.checkInProgress) {
        if (cache.lastResponse) {
          // Buscar en la última respuesta cacheada
          const productosCache = cache.lastResponse.productos || [];
          const productoEnCache = productosCache.find(item => String(item.plu) === pluStr);

          if (productoEnCache) {
            const result = {
              estaEnCarrito: true,
              cantidad: productoEnCache.cantCarrito || 1,
              producto: productoEnCache,
              cached: true
            };
            return result;
          }
        }
        return { estaEnCarrito: false, cantidad: 0, cached: true };
      }

      // Marcar que hay una verificación en progreso para evitar llamadas paralelas
      cache.checkInProgress = true;

      // 5. Usar cargarProductosCarritoSafe que ya tiene protección contra llamadas excesivas
      try {
        const { success, productos: productosResult, cached } = await cargarProductosCarritoSafe();
        if (!success || !productosResult) {
          return { estaEnCarrito: false, cantidad: 0, cached };
        }

        // Buscar el producto en el resultado
        const productoEnCarrito = productosResult.find(item => String(item.plu) === pluStr);

        const result = productoEnCarrito ?
          {
            estaEnCarrito: true,
            cantidad: productoEnCarrito.cantCarrito || 1,
            producto: productoEnCarrito,
            cached: cached
          } :
          { estaEnCarrito: false, cantidad: 0, cached: cached };

        // Guardar en caché local
        productosCacheRef.current.set(pluStr, {
          timestamp: now,
          data: result
        });

        return result;
      } finally {
        // Liberar la verificación en progreso después de un tiempo
        setTimeout(() => {
          cache.checkInProgress = false;
        }, 500);
      }
    } catch (err) {
      console.error('Error al verificar producto en carrito:', err);
      return { estaEnCarrito: false, cantidad: 0, error: err.message };
    }
  };

  // Referencia para evitar llamadas múltiples a agregar al carrito
  const agregarCarritoRef = useRef({ loading: false, lastCall: 0 });

  // Función para vaciar todo el carrito
  const vaciarCarrito = async () => {
    try {
      setLoading(true);
      setError(null);

      const { mail, codUsu, suc } = await getUserData();

      // Si no hay mail, no podemos vaciar el carrito
      if (!mail) {
        setError('Usuario no autenticado');
        return { success: false, error: 'Usuario no autenticado' };
      }

      // Construir la URL con query parameters
      const queryParams = new URLSearchParams({ mail, codUsu, suc }).toString();
      const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
        },
      });

      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        data = { message: responseText };
      }

      if (response.ok) {
        // Limpiar el estado local
        setProductos([]);
        setTotales({ subtotal: 0, descuentos: 0, total: 0, precioFlete: 0, percepciones: 0 });

        // Limpiar caché
        cacheRef.current.lastResponse = { success: true, productos: [] };
        cacheRef.current.timestamp = Date.now();
        productosCacheRef.current.clear();

        // Mostrar mensaje de éxito solo en móvil
        if (Platform.OS !== 'web') {
          Alert.alert(
            "¡Carrito vaciado!",
            "Todos los productos han sido eliminados del carrito."
          );
        }

        emitCarritoActualizado();

        return { success: true, data };
      } else {
        const errorMsg = (data && data.message) || `Error ${response.status}: No se pudo vaciar el carrito.`;
        setError(errorMsg);

        if (Platform.OS !== 'web') {
          Alert.alert("Error", errorMsg);
        }

        return { success: false, error: errorMsg };
      }
    } catch (error) {
      const errorMsg = `Error al vaciar el carrito: ${error.message}`;
      setError(errorMsg);

      if (Platform.OS !== 'web') {
        Alert.alert("Error", errorMsg);
      }

      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Función para agregar o actualizar un producto en el carrito con protección de frecuencia
  const agregarAlCarrito = async (plu, cantidad, sucursalOverride) => {
    try {
      const now = Date.now();
      const ref = agregarCarritoRef.current;

      // Si ya está ejecutando una operación o ha pasado muy poco tiempo
      if (ref.loading || (now - ref.lastCall < THROTTLE_DELAY)) {
        return { success: false, error: 'Operación en progreso, por favor espere' };
      }

      ref.loading = true;
      ref.lastCall = now;
      setLoading(true);

      const { mail, codUsu, suc } = await getUserData();

      // Construir la URL con query parameters
      const queryParams = new URLSearchParams({
        mail: mail || '',
        codUsu: codUsu || '',
        suc: suc,
      }).toString();

      const body = {
        
          ttCarrito: [
            {
              plu: plu, cant: cantidad
            }
          ]
        
      };

      const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });




      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        data = { message: responseText };
      }

      // Verificar si la respuesta tiene resultado: false aunque response.ok sea true
      if (data?.dsResultado?.resultado && Array.isArray(data.dsResultado.resultado)) {
        const resultado = data.dsResultado.resultado[0];
        if (resultado && resultado.resultado === false) {
          // Mostrar modal de error con el mensaje
          const mensajeError = resultado.mensaje || 'No se pudo agregar el producto al carrito';
          setFeedbackTitle('Productos incompatibles');
          setFeedbackMessage(mensajeError);
          setFeedbackVisible(true);
          
          return { success: false, error: mensajeError, data };
        }
      }

      if (response.ok) {
        // Verificar si hay productos borrados (puede venir en POST carrito)
        if (data?.dsResultado?.productosBorrados && data.dsResultado.productosBorrados.length > 0) {
          setProductosBorrados(data.dsResultado.productosBorrados);
          setMostrarModalBorrados(true);
        }

        // Verificar si hay productos faltantes (puede venir en POST carrito)
        if (data?.dsResultado?.productosFaltantes && data.dsResultado.productosFaltantes.length > 0) {
          setProductosFaltantes(data.dsResultado.productosFaltantes);
          setMostrarModalFaltantes(true);
        }

        // Mostrar mensaje de éxito solo en móvil
        if (Platform.OS !== 'web') {
          Alert.alert(
            "¡Agregado con éxito!",
            "El producto se ha agregado a tu carrito."
          );
        }

        // Invalidar caché para forzar recarga real
        cacheRef.current.timestamp = 0;
        cacheRef.current.lastResponse = null;
        productosCacheRef.current.clear();

        // Forzar recarga del carrito sin throttle
        cargarProductosRef.current.loading = false;
        cargarProductosRef.current.lastCall = 0;
        await cargarProductosCarritoSafe();

        return { success: true, data };
      } else {
        const errorMsg = (data && data.message) || `Error ${response.status}: Ocurrió un error al modificar el carrito.`;
        setError(errorMsg);

        // Mostrar error solo en móvil
        if (Platform.OS !== 'web') {
          Alert.alert("Error", errorMsg);
        }

        return { success: false, error: errorMsg };
      }
    } catch (error) {
      const errorMsg = `Error al modificar el carrito: ${error.message}`;
      setError(errorMsg);

      if (Platform.OS !== 'web') {
        Alert.alert("Error", errorMsg);
      }

      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
      // Esperar un momento antes de permitir otra operación
      setTimeout(() => {
        agregarCarritoRef.current.loading = false;
      }, THROTTLE_DELAY);
    }
  };

  // Función para calcular el total (usar desde la API o calcularlo si no está disponible)
  const calcularTotal = () => {
    // Si tenemos el total de la API, usamos ese
    if (totales.total) {
      return totales.total;
    }

    // Si no, lo calculamos manualmente
    return productos.reduce((total, producto) => {
      return total + (producto.precio * (producto.cantCarrito || 1));
    }, 0) || 0;
  };

  // Función para mostrar/ocultar el carrito
  const toggleCarrito = () => {
    setCarritoVisible(prev => !prev);
  };

  // Referencia para evitar llamadas múltiples en corto tiempo
  const cargarProductosRef = useRef({ loading: false, lastCall: 0, retryCount: 0 });
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 5000; // 5 segundos entre reintentos
  const THROTTLE_DELAY = 1000; // Mínimo 1 segundo entre llamadas

  // Sistema de caché para datos del carrito
  const cacheRef = useRef({
    lastResponse: null,         // Última respuesta completa de la API
    timestamp: 0,              // Cuándo se obtuvo la última respuesta
    checkInProgress: false,    // Si hay una verificación en progreso
    eventsPaused: false,       // Bandera para pausar la emisión de eventos
    CACHE_DURATION: 3000,      // 3 segundos de validez para la caché
    blockedUntil: 0           // Timestamp hasta el cual se bloquean las llamadas
  });

  // Cargar datos del carrito cuando el componente se monta
  useEffect(() => {
    // Primera carga controlada una sola vez al inicio
    cargarProductosCarritoSafe();

    // YA NO NECESITAMOS ESCUCHAR EVENTOS EXTERNOS - El contexto es la fuente única de verdad
    // Eliminamos el event listener para romper el ciclo de eventos
  }, []);

  // Versión segura de cargarProductosCarrito con caché y control estricto de frecuencia
  const cargarProductosCarritoSafe = useCallback(async () => {
    const now = Date.now();
    const ref = cargarProductosRef.current;
    const cache = cacheRef.current;

    // Mecanismo de bloqueo temporal - si estamos en periodo de bloqueo, no permitir llamadas
    if (now < cache.blockedUntil) {
      return cache.lastResponse || { success: false, productos: [], cached: true };
    }

    // Si ya está cargando o ha pasado muy poco tiempo desde la última llamada, usar caché
    if (ref.loading || (now - ref.lastCall < THROTTLE_DELAY)) {
      if (cache.lastResponse) {
        return { ...cache.lastResponse, cached: true };
      }
      return { success: false, productos: [], cached: true };
    }

    // Usar datos en caché si son recientes (dentro del tiempo de validez de la caché)
    if (cache.lastResponse && now - cache.timestamp < cache.CACHE_DURATION) {
      return { ...cache.lastResponse, cached: true };
    }

    ref.loading = true;
    ref.lastCall = now;

    try {
      const result = await cargarProductosCarrito();
      // Guardar en caché solo si fue exitoso
      if (result.success) {
        cache.lastResponse = result;
        cache.timestamp = now;
        // Reiniciar contador de reintentos
        ref.retryCount = 0;
      }

      return result;
    } catch (error) {
      console.error('Error al cargar productos del carrito:', error);

      // Si hay muchos errores, establecer un periodo de bloqueo para evitar saturación
      ref.retryCount++;
      if (ref.retryCount >= 3) {
        const blockDuration = Math.min(5000 * Math.pow(2, ref.retryCount - 3), 60000); // Máx 1 minuto
        cache.blockedUntil = now + blockDuration;
        return cache.lastResponse || { success: false, productos: [], blocked: true };
      }

      // Si la caché está disponible, usarla incluso después de un error
      if (cache.lastResponse) {
        return { ...cache.lastResponse, cached: true };
      }

      return { success: false, error: error.message, productos: [] };
    } finally {
      // Marcar como no cargando
      setTimeout(() => {
        ref.loading = false;
      }, THROTTLE_DELAY);
    }
  }, []);

  // Funciones para manejar el modal de productos faltantes
  const aceptarCambioSucursal = useCallback(() => {
    // Usuario acepta continuar sin los productos faltantes
    setMostrarModalFaltantes(false);
    setProductosFaltantes([]);
    setSucursalAnterior(null);
  }, []);

  // Funciones para manejar el modal de productos borrados
  const cerrarModalBorrados = useCallback(() => {
    setMostrarModalBorrados(false);
    setProductosBorrados([]);
  }, []);

  const rechazarCambioSucursal = useCallback(async () => {
    // Usuario rechaza el cambio, volver a la sucursal anterior
    if (sucursalAnterior) {
      try {
        // Restaurar la sucursal anterior en localStorage
        await Storage.setItem('sucursalId', sucursalAnterior.sucursalId);
        await Storage.setItem('ciudadNom', sucursalAnterior.ciudadNom);
        await Storage.setItem('selectedSubBranch', sucursalAnterior.selectedSubBranch);
        await Storage.setItem('ciudadNom', sucursalAnterior.ciudadNom);
        
        // Disparar eventos para actualizar otros componentes
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('localStorageChange', {
            detail: { key: 'sucursalId', newValue: sucursalAnterior.sucursalId }
          }));
          window.dispatchEvent(new CustomEvent('localStorageChange', {
            detail: { key: 'ciudadNom', newValue: sucursalAnterior.ciudadNom }
          }));
        }
        
        // Re-agregar todos los productos faltantes al carrito en una sola llamada
        if (productosFaltantes && productosFaltantes.length > 0) {
          const { mail, codUsu, suc } = await getUserData();
          
          // Construir array de productos con plu y cantidad
          const productosParaAgregar = productosFaltantes.map(producto => ({
            plu: producto.plu,
            cant: producto.cant || producto.cantidad || 1 // Usar cant, cantidad o 1 por defecto
          }));

          const queryParams = new URLSearchParams({
            mail: mail || '',
            codUsu: codUsu || '',
            suc: sucursalAnterior.sucursalId,
          }).toString();

          const body = {
            ttCarrito: productosParaAgregar
          };

          const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });

          if (!response.ok) {
            console.error('Error al re-agregar productos:', response.status);
          }
        }
        
        // Recargar el carrito con la sucursal anterior
        await cargarProductosCarrito();
      } catch (error) {
        console.error('Error al restaurar sucursal anterior:', error);
      }
    }
    
    setMostrarModalFaltantes(false);
    setProductosFaltantes([]);
    setSucursalAnterior(null);
  }, [sucursalAnterior, productosFaltantes]);

  return (
    <CartContext.Provider value={{
      // Estado
      productos,
      carritoVisible,
      loading,
      error,
      totales,
      productosFaltantes,
      mostrarModalFaltantes,
      productosBorrados,
      mostrarModalBorrados,

      // Operaciones del carrito
      cargarProductosCarrito: cargarProductosCarritoSafe, // Exponer la versión segura
      verificarProductoEnCarrito,
      agregarAlCarrito,
      vaciarCarrito,
      calcularTotal,
      toggleCarrito,
      setCarritoVisible,

      // Utilidades
      emitCarritoActualizado,
      recargarCarritoManual, // Nueva función de carga manual
      aceptarCambioSucursal,
      rechazarCambioSucursal,
      setSucursalAnterior,
      cerrarModalBorrados,

      // Estado interno (para depuración)
      cacheStatus: {
        hayCache: !!cacheRef.current.lastResponse,
        ultimaActualizacion: cacheRef.current.timestamp,
        bloqueadoHasta: cacheRef.current.blockedUntil
      }
    }}>
      {children}
      
      {/* Modal de productos faltantes */}
      <ProductosFaltantesModal
        visible={mostrarModalFaltantes}
        productosFaltantes={productosFaltantes}
        onAceptar={aceptarCambioSucursal}
        onRechazar={rechazarCambioSucursal}
      />

      {/* Modal de productos borrados */}
      <ProductosBorradosModal
        visible={mostrarModalBorrados}
        productosBorrados={productosBorrados}
        onClose={cerrarModalBorrados}
      />
      
      {/* Modal de feedback para errores de productos incompatibles */}
      <FeedbackModal
        visible={feedbackVisible}
        type="error"
        title={feedbackTitle}
        message={feedbackMessage}
        confirmText="Entendido"
        onClose={() => setFeedbackVisible(false)}
      />
    </CartContext.Provider>
  );
};

// Hook personalizado para usar el contexto del carrito
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe ser usado dentro de un CartProvider');
  }
  return context;
};
